import { useState, useCallback } from "react";

import { MapPin, Trash2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

import { Dialog, DialogContent } from "@/modern/components/ui/dialog";
import { cn } from "@/modern/utils";
import { EntityLocationView } from "@/react/components/entityViews/location/EntityLocationView";

import { useFileDetailModal } from "../../../context/FileDetailModalContext";
import { SearchTextLink } from "../SearchTextLink";
import { ExtractedLocation } from "../types";

interface LocationExtractionResultsProps {
  locations: ExtractedLocation[];
}

interface LocationCardState {
  dismissed: boolean;
}

function formatLocationAddress(location: ExtractedLocation): string {
  return (
    [location.address, location.city, location.region, location.country].filter(Boolean).join(", ") ||
    location.place_name ||
    location.query
  );
}

export const LocationExtractionResults = ({ locations }: LocationExtractionResultsProps) => {
  const { project, entityRecord } = useFileDetailModal();

  const [cardStates, setCardStates] = useState<Record<number, LocationCardState>>(() => {
    const initial: Record<number, LocationCardState> = {};
    locations.forEach((_, index) => {
      initial[index] = { dismissed: false };
    });
    return initial;
  });

  const [locationDialog, setLocationDialog] = useState<{ location: ExtractedLocation; index: number } | null>(null);

  const updateCardState = useCallback((index: number, update: Partial<LocationCardState>) => {
    setCardStates((prev) => ({
      ...prev,
      [index]: { ...prev[index], ...update },
    }));
  }, []);

  const handleOpenLocationDialog = useCallback(
    (location: ExtractedLocation, index: number) => {
      setLocationDialog({ location, index });
    },
    []
  );

  const handleLocationDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setLocationDialog(null);
        project?.sync();
      }
    },
    [project]
  );

  const handleDismiss = useCallback((index: number) => {
    updateCardState(index, { dismissed: true });
  }, [updateCardState]);

  const formatLocationDetails = (location: ExtractedLocation): string => {
    const parts = [location.city, location.region, location.country].filter(Boolean);
    return parts.join(", ");
  };

  const visibleLocations = locations.filter((_, index) => !cardStates[index]?.dismissed);

  if (visibleLocations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <p className="text-sm text-muted-foreground">All location cards have been dismissed.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      <AnimatePresence>
        {locations.map((location, index) => {
          const state = cardStates[index];
          if (state?.dismissed) return null;

          const details = formatLocationDetails(location);

          return (
            <motion.div
              key={index}
              layout
              initial={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="border border-border rounded-swivu-base overflow-hidden"
            >
              <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col min-w-0 flex-1">
                    {/* Place name or query */}
                    <span className="text-sm font-medium text-foreground">
                      {location.place_name || location.query}
                    </span>

                    {/* Address */}
                    {location.address && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {location.address}
                      </p>
                    )}

                    {/* City, Region, Country */}
                    {details && (
                      <p className="text-xs text-muted-foreground/70 mt-0.5">
                        {details}
                      </p>
                    )}
                  </div>

                  {/* Action icons */}
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      title="Edit location"
                      onClick={() => handleOpenLocationDialog(location, index)}
                      className={cn(
                        "relative flex items-center justify-center w-7 h-7 rounded-md transition-all duration-150",
                        "hover:bg-primary/10 hover:text-primary active:bg-primary/20"
                      )}
                    >
                      <MapPin className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      title="Dismiss"
                      onClick={() => handleDismiss(index)}
                      className={cn(
                        "relative flex items-center justify-center w-7 h-7 rounded-md transition-all duration-150",
                        "hover:bg-destructive/10 hover:text-destructive active:bg-destructive/20"
                      )}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Search text link */}
                {location.search_text && (
                  <div className="mt-2">
                    <SearchTextLink searchText={location.search_text} />
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      <Dialog open={!!locationDialog} onOpenChange={handleLocationDialogOpenChange}>
        <DialogContent
          className="z-[--swivu-bryntum-z-index] max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0"
          dialogOverlayClassName="z-[--swivu-bryntum-z-index]"
          showCloseButton={false}
        >
          {locationDialog && entityRecord && (
            <div className="flex-1 min-h-0 overflow-auto">
              <EntityLocationView
                record={entityRecord}
                initialAddress={formatLocationAddress(locationDialog.location)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
